import { useMemo, useState, useEffect } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import { Box } from '@mui/material';
import styles from './ListOfUsersTable.module.css'
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Modal from 'react-modal';
import DeleteButton from '../DeleteButton/DeleteButton';
import CancelButton from '../CancelButton/CancelButton';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import { useQuery } from '@apollo/client';
import { GET_USER_PROFILES, GET_TIER } from '../../services/graphql';
import useProfileQuery from '../../hooks/useProfileQuery';
import { getTableDensityStyles, normalizeTableDensity } from '../../config/tableDensity';

const ListOfUsersTable = ({activeTab, showColumnFilters, globalFilter, onGlobalFilterChange, columnOrder}) => {
  const navigate = useNavigate();
  const isDarkMode = useSelector(state => state.graphql.darkMode);
  const [data, setData] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnSorts, setColumnSorts] = useState([]);
  const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 5,
  });
  const [rowCount, setRowCount] = useState(0); 
  const tableDensity = normalizeTableDensity(localStorage.getItem('tableDensity'));
  const densityStyles = getTableDensityStyles(tableDensity);
  const email = sessionStorage.getItem("selectedUser");
  const { deleteUser } = useProfileQuery(email);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
      },
      {
        accessorKey: 'organization',
        header: 'Organization',
        size: 200,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 150,
      },
      {
        accessorKey: 'accessLevel',
        header: 'Access Level',
        size: 175,
      },
      {
        accessorKey: 'tier',
        header: 'Tier',
        size: 75,
      },
    ],
    [],
  );


  function convertAndStringifyFilters(inputArray) {
    let andFilter = [];

    if (columnFilters.length > 0) {
        const convertedArray = inputArray.map(inputObject => {
            return {
                field: inputObject.id,
                operation: "contains",
                value: inputObject.value
            };
        });
        andFilter = andFilter.concat(convertedArray);
    }

    if (activeTab === 'managers') {
      andFilter.push({
          field: 'accessLevel',
          operation: 'contains',
          value: 'Manager'
      });
    } else if (activeTab === 'approvers') {
      andFilter.push({
        field: 'accessLevel',
        operation: 'contains',
        value: 'Approver'
      });
    } else if (activeTab === 'suppliers') {
      andFilter.push({
        field: 'accessLevel',
        operation: 'contains',
        value: 'Supplier'
      });
    }

    return JSON.stringify(andFilter);
  }

  function convertAndStringifyGlobalFilter(globalFilter) {
      // or filter for all selected columns
      const convertedArray = columns.map(column => {
          return {
              field: column.accessorKey || column.id,
              operation: "contains",
              value: globalFilter,
          };
      });

      return JSON.stringify(convertedArray);
  }

  const { loading, error, data: data1, refetch } = useQuery(GET_USER_PROFILES);
  
  if (error) {
      alert('Unable to load users.');
  }

  const getTier = useQuery(GET_TIER);

  useEffect(() => {
      const activeSort = columnSorts.length > 0 ? columnSorts[0] : null;
      let variables = {
          page: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
          sortBy: activeSort ? activeSort.id : null,
          sortOrder: activeSort ? (activeSort.desc ? "DESC" : "ASC") : null,
          filter: {
              andFilter: null,
              orFilter: null,
          }
      };

      // Tier is computed client-side, so skip server sorting for that column.
      if (activeSort?.id === "tier") {
        variables.sortBy = null;
        variables.sortOrder = null;
      }

      // to obtain the total number of users with the current filter
      let variablesTotal = {
    page: 1,
        pageSize: Infinity,
        filter: {
            andFilter: null,
            orFilter: null,
        }
      };

      const andFilter = convertAndStringifyFilters(columnFilters);
      const orFilter = globalFilter && convertAndStringifyGlobalFilter(globalFilter);

      if (andFilter) { 
        variables.filter.andFilter = andFilter; 
        variablesTotal.filter.andFilter = andFilter;
      }
      else { 
          variables.filter.andFilter = null; 
          variablesTotal.filter.andFilter = null;
      }

      if (orFilter) { 
          variables.filter.orFilter = orFilter; 
          variablesTotal.filter.orFilter = orFilter; 
      }
      else { 
          variables.filter.orFilter = null; 
          variablesTotal.filter.orFilter = null; 
      }

      // to get the total # of invoices with the set filtering, and set row count for correct front-end pagination
      refetch(variablesTotal)
      .then(({ data }) => {
          setRowCount(data.getUserProfiles.body.length);
      })
      .catch(error => {
          console.error('Error during refetch:', error);
      });

      refetch(variables)
      .then(async ({ data }) => {
        const updatedDataPromise = data.getUserProfiles.body.map(async (profile) => {
          let tier = '';
          var profileEmail = profile.email;
          const accessType = profile.accessLevel;

          if (accessType.startsWith('Approver')) {
            try {
              const { data: tierData } = await getTier.refetch({ email: profileEmail });
              tier = tierData.getApprover.body.tierNumber;
            } catch (error) {
              console.error("Error getting tier:", error);
            }
          }
          return {
            ...profile,
            tier
          };
        });
        const updatedData = await Promise.all(updatedDataPromise);
        if (activeSort?.id === "tier") {
          const direction = activeSort.desc ? -1 : 1;
          updatedData.sort((a, b) => {
            const aTier = Number(a.tier) || 0;
            const bTier = Number(b.tier) || 0;
            return (aTier - bTier) * direction;
          });
        }
        setData(updatedData);
      })
      .catch(error => {
          console.error('Error during refetch:', error);
      });
  }, [pagination, columnFilters, columnSorts, globalFilter, activeTab]);

  const customIcons = {
    SearchIcon: () => <SearchIcon color='primary' />,
    SearchOffIcon: () => <SearchIcon color='primary' />,
    FilterListIcon: () => <TuneIcon color='primary' />,
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const customModalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'var(--theme-bg-elevated)',
      color: 'var(--theme-text)',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      position: 'fixed',
      zIndex: 5,
    },
  };

  useEffect(() => {
    if (deleted) {
      setIsModalOpen(true);
    }
  }, [deleted]);

  const closeModal = () => {
    navigate(`/user-management`);
    setIsModalOpen(false);
  };

  const handleDelete = () => {
      //Opens Delete Confirmation window
      setDeleted(true);
  };

  const handleConfirmDelete = async () => {
    try {
        let email = sessionStorage.getItem("selectedUser");
        console.log("############## Deleting user with email:", email);
        await deleteUser(email);
        setIsModalOpen(false);
        setDeleted(false);
        window.location.reload();
        navigate(`/user-management`);
    } catch (error) {
        console.error("Error deleting user:", error);
    }
  };

  const pagedData = useMemo(() => {
  const start = pagination.pageIndex * pagination.pageSize;
  const end = start + pagination.pageSize;
  return data.slice(start, end);
}, [data, pagination]);

  const table = useMaterialReactTable({
    columns,
    data : pagedData,
    initialState: { density: densityStyles.density, showColumnFilters: false },
    muiTableHeadRowProps: {
        sx: { backgroundColor: 'var(--theme-bg-elevated)' },
    },
    muiTableHeadCellProps: ({ table }) => ({
      sx: table.getState().density === 'compact'
        ? densityStyles.headCell
        : {},
    }),
    muiTableBodyCellProps: ({ table }) => ({
      sx: table.getState().density === 'compact'
        ? densityStyles.bodyCell
        : {},
    }),
    mrtTheme: {
        baseBackgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
    },
    muiTablePaperProps: {
        sx: { backgroundColor: 'var(--theme-bg-elevated)' },
    },
    muiBottomToolbarProps: {
        sx: { backgroundColor: 'var(--theme-bg-elevated)', color: 'var(--theme-text)' },
    },
    enableTopToolbar: false,
    enableColumnResizing: true,
    enableColumnOrdering: true,
    enableRowActions: true,
    icons: customIcons,
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: densityStyles.actionsColumnSize,
      },
    },
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '2vw' }}>
        <button className={styles.deleteButton} style={densityStyles.actionButton} onClick={() => {
          handleDelete();
          sessionStorage.setItem("selectedUser", row.original['email']);
        }}>
          Delete
        </button>
        <button className={styles.editButton} style={densityStyles.actionButton} onClick={() => {
          sessionStorage.setItem("selectedUser", row.original['email']);
          navigate(`/user-management/user/${row.original['organization']}/${row.original['name']}`, {state: {isEdit: true}});
        }}>
          Edit
        </button>
      </Box>
    ),
    background: {
      default:
        '#F2EFEF',
    },
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      showRowsPerPage: true,
      variant: 'outlined',
    },
    paginationDisplayMode: 'pages',
    enableHiding: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    manualFiltering: true,  // server-side filtering
    manualPagination: true, //server-side pagination
    manualSorting: true, //server-side sorting
    rowCount,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    onGlobalFilterChange: onGlobalFilterChange,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onSortingChange: setColumnSorts,
    state: {
        columnFilters,
        pagination,
        sorting: columnSorts,
        globalFilter,
        showColumnFilters,
        showSkeletons: loading,
        columnOrder,
    },
    muiTableBodyRowProps: ({ row }) => ({
        onDoubleClick: () => {
            const name = row.original['name'];
            const organization = row.original['organization'];
            sessionStorage.setItem("selectedUser", row.original['email']);
            navigate(`/user-management/user/${organization}/${name}`);
        },
        sx: {
            cursor: 'pointer',
            backgroundColor: 'var(--theme-bg-elevated)',
            ...densityStyles.bodyRow,
        },
    }),
  });

  return (<div>
    <MaterialReactTable table={table} />

    <Modal
      isOpen={isModalOpen}
      onRequestClose={closeModal}
      style={customModalStyles}
      contentLabel="Delete Confirmation"
    >
      <button className={styles.closeButton} onClick={closeModal}>
        <span className={styles.closeIcon}>x</span>
      </button>
      <h2 className={styles.modalTitle}>Delete User</h2>
      <p className={styles.confirmMessage}>Are you sure you want to delete this user?</p>
      <p className={styles.confirmMessage}>You will not be able to undo this action.</p>
      <DeleteButton handleDelete={handleConfirmDelete} />
      <CancelButton label="Cancel" />
    </Modal>
  </div>

  )
};

export default ListOfUsersTable;
